<template>
  <div v-if="showNotesComponent" class="current-session-div">
    <div class="backicon-notes-div">
      <span class="material-symbols-outlined hover-effect" @click="showTBCurrentSessionFunction()">chevron_left</span>
      <span class="label-span">Notes</span>
    </div>
    <div>
      <Textarea v-model="notesValue" rows="9" cols="27" placeholder="Add Notes..."
        @keydown.tab="showTBCurrentSessionFunction" />
    </div>
  </div>
  <div v-else-if="showTNMComponent" class="current-session-div">
    <div class="backicon-notes-div">
      <span class="material-symbols-outlined hover-effect" @click="showTBCurrentSessionFunction()">chevron_left</span>
      <span class="label-span">TNM</span>
    </div>
    <div>
      <Textarea v-model="tnmValue" rows="9" cols="27" placeholder="Add TNM..."
        @keydown.tab="showTBCurrentSessionFunction" />
    </div>
  </div>
  <div v-else>
    <div id="current-session" class="current-session-div">
      <div>
        <span class="label-span">Diagnosis</span>
      </div>
      <div class="current-session-dropdown-div">
        <Dropdown v-model="selectedDiagnosis" :options="patientDiagnosisDropdown" optionLabel="description"
          placeholder="Diagnosis Not Assigned" class="current-session-multiselect" />
      </div>
      <div>
        <span class="label-span">Discussion</span>
      </div>
      <div class="current-session-multiselect-div">
        <MultiSelect v-model="selectedDiscussion" :options="recordDiscussionDropdown" :showToggleAll="false"
          optionLabel="label" placeholder="Select Discussion" class="current-session-multiselect" />
      </div>
      <div>
        <span class="label-span">Clinical Stage</span>
      </div>
      <div class="current-session-dropdown-div">
        <Dropdown v-model="selectedClinicalStage" :options="recordClinicalStageDropdown" optionLabel="label"
          placeholder="Select Clinical Stage" class="custom-placeholder-color" />
      </div>
      <div>
        <span class="label-span">Referrals</span>
      </div>
      <div class="current-session-multiselect-div">
        <MultiSelect v-model="selectedReferral" :options="recordReferralDropdown" :showToggleAll=false optionLabel="label"
          placeholder="Select Referrals" class="current-session-multiselect" />
      </div>
      <div>
        <span class="label-span">Recommendations</span>
      </div>
      <div class="current-session-multiselect-div">
        <MultiSelect v-model="selectedRecommendation" :showToggleAll=false :options="recordRecommendationsDropdown"
          optionLabel="label" placeholder="Select Recommendations" class="current-session-multiselect" />
      </div>
      <div>
        <span class="label-span">TNM</span>
      </div>
      <div class="current-session-notes-button-div">
        <Button :label="dynamicTNMButtonLabel" outlined @click="showTNMComponentFunction()" size="small"
          class="custom-placeholder-color" />
      </div>
      <div>
        <span class="label-span">Notes</span>
      </div>
      <div class="current-session-notes-button-div">
        <Button :label="dynamicNotesButtonLabel" outlined @click="showNotesComponentFunction()" size="small"
          class="custom-placeholder-color" />
      </div>
      <div class="current-session-save-button-div">
        <div>
          <button @click="handleRecordSave()" :disabled="isSaveClicked || isSaveDisabled" class="primary-filled-button">
            {{ saveButtonLabel }}
          </button>
        </div>
        <div>
          <button v-if="isSaveClicked && hasNextPatient" @click="handleNextPatient()"
            class="primary-filled-button button-class">
            Next Patient
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 Omics Data Automation, Inc.
// Copyright (c) 2023 datma, inc.
//
// Proprietary and Confidential:
// Unauthorized copying of this file by any medium is strictly prohibited.

import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { getRecordData } from './getRecordData'
import { patchRecordData } from './patchRecordData'
import MultiSelect from 'primevue/multiselect'
import Dropdown from 'primevue/dropdown'
import Textarea from 'primevue/textarea'
import Button from 'primevue/button'
import { addDefaultDiagnosis, formatDateToTBPayload, showToast } from '@/utils/utils.js'
import { priorityOptions } from '@/common/shared.js'
import { patchPatientSchedule } from '@/ux3/cards/TumorBoardSideBar/patchPatientSchedule'

const store = useStore()

const recordDiscussionDropdown = ref([])
const selectedDiscussion = ref([])
const selectedClinicalStage = ref(null)
const selectedReferral = ref([])
const selectedRecommendation = ref([])
const patientDiagnosisDropdown = ref([])
const selectedDiagnosis = ref('')
const recordClinicalStageDropdown = ref([])
const recordReferralDropdown = ref([])
const recordRecommendationsDropdown = ref([])
const selectedPriority = ref('')
const showNotesComponent = ref(false)
const showTNMComponent = ref(false)
const dynamicNotesButtonLabel = ref('Click to Add Notes')
const dynamicTNMButtonLabel = ref('Click to Add TNM')
const notesValue = ref('')
const tnmValue = ref('')
const scheduleId = ref('')
const isSaveClicked = ref(false)
const initialPayloadObject = ref({})

const patient = computed(() => store.getters.session.patient)
const patientsFromAgenda = computed(() => store.getters.session.patientsFromAgenda)
const comparePayloadObject = computed(() => {
  return {
    diagnosis: selectedDiagnosis.value,
    discussion: selectedDiscussion.value,
    clinicalStage: selectedClinicalStage.value,
    referral: selectedReferral.value,
    recommendation: selectedRecommendation.value,
    priority: selectedPriority.value,
    notes: notesValue.value,
    tnmValue: tnmValue.value,
  }
})
const saveButtonLabel = computed(() => (isSaveClicked.value ? 'Saved' : 'Save'))
const hasNextPatient = computed(() => {
  const nextPatientIndex = patientsFromAgenda.value.findIndex((patientItem) => patientItem.id === patient.value.id)
  return nextPatientIndex !== -1 && nextPatientIndex < patientsFromAgenda.value.length - 1
})
const isSaveDisabled = computed(() => {
  return (
    !selectedDiscussion.value.length &&
    !selectedClinicalStage.value &&
    !selectedReferral.value.length &&
    !selectedRecommendation.value.length &&
    !selectedPriority.value &&
    !notesValue.value &&
    !tnmValue.value
  )
})

const showNotesComponentFunction = () => {
  showNotesComponent.value = true
}

const showTNMComponentFunction = () => {
  showTNMComponent.value = true
}

const showTBCurrentSessionFunction = () => {
  showNotesComponent.value = false
  showTNMComponent.value = false
  dynamicNotesButtonLabel.value = notesValue.value || 'Click to Add Notes'
  dynamicTNMButtonLabel.value = tnmValue.value || 'Click to Add TNM'
}

watch(patient, async (newPatient) => {
  if (!newPatient.schedule_id) { return }
  scheduleId.value = newPatient.schedule_id
  const recordData = await getRecordData(newPatient.schedule_id)
  if (!recordData) { return }
  const discussionOptionsArray = Object.keys(recordData[0]?.recording_info?.discussion ?? {})
  const discussionOptions = discussionOptionsArray.map((item) => ({
    value: item,
    label: item,
  }))
  recordDiscussionDropdown.value = discussionOptions
  selectedDiscussion.value = filterKeysWithTrueValues(recordData[0]?.recording_info?.discussion ?? {}).map((item) => ({ value: item, label: item }))

  patientDiagnosisDropdown.value = addDefaultDiagnosis(newPatient.diagnosisArray)

  const diagnosisName = newPatient.diagnosis
  selectedDiagnosis.value = patientDiagnosisDropdown.value.find(diagnosis => diagnosis.description === diagnosisName)

  selectedPriority.value = priorityOptions.find(option => option.priority === newPatient.priority)

  const clinicalStageOptionsArray = Object.keys(recordData[0]?.recording_info?.clinical_staging ?? {})
  const clinicalStageOptions = clinicalStageOptionsArray.map((item) => ({
    value: item,
    label: item,
  }))
  recordClinicalStageDropdown.value = clinicalStageOptions
  const clinicalStagingKeyValue = filterKeysWithTrueValues(recordData[0]?.recording_info?.clinical_staging ?? {}).map((item) => ({ value: item, label: item }))
  selectedClinicalStage.value = clinicalStagingKeyValue[0]

  const referralOptionsArray = Object.keys(recordData[0]?.recording_info?.referrals ?? {})
  const referralOptions = referralOptionsArray.map((item) => ({
    value: item,
    label: item,
  }))
  recordReferralDropdown.value = referralOptions
  selectedReferral.value = filterKeysWithTrueValues(recordData[0]?.recording_info?.referrals ?? {}).map((item) => ({ value: item, label: item }))

  const recommendationsOptionsArray = Object.keys(recordData[0]?.recording_info?.recommendations ?? {})
  const recommendationsOptions = recommendationsOptionsArray.map((item) => ({
    value: item,
    label: item,
  }))
  recordRecommendationsDropdown.value = recommendationsOptions
  selectedRecommendation.value = filterKeysWithTrueValues(recordData[0]?.recording_info?.recommendations ?? {}).map((item) => ({ value: item, label: item }))

  notesValue.value = recordData[0]?.recording_info?.notes
  dynamicNotesButtonLabel.value = recordData[0]?.recording_info?.notes || 'Click to Add Notes'

  tnmValue.value = recordData[0]?.recording_info?.tnm
  dynamicTNMButtonLabel.value = recordData[0]?.recording_info?.tnm || 'Click to Add TNM'

  initialPayloadObject.value = {
    diagnosis: selectedDiagnosis.value,
    discussion: selectedDiscussion.value,
    clinicalStage: selectedClinicalStage.value,
    referral: selectedReferral.value,
    recommendation: selectedRecommendation.value,
    priority: selectedPriority.value,
    notes: notesValue.value,
    tnmValue: tnmValue.value,
  }
}, { immediate: true })

watch(comparePayloadObject, (newComparePayloadObject) => {
  if (!newComparePayloadObject) { return }
  isSaveClicked.value = JSON.stringify(initialPayloadObject.value) === JSON.stringify(newComparePayloadObject)
})

const getObjectFromArray = (arr) => {
  return arr.reduce((obj, item) => (obj[item.label] = true, obj), {})
}

function filterKeysWithTrueValues(inputObject) {
  return Object.keys(inputObject).filter((key) => inputObject[key] === true)
}

const handleRecordSave = async () => {
  const key = selectedClinicalStage.value?.label || selectedClinicalStage?.value || ''
  const clinicalObject = {}
  if (key) { clinicalObject[key] = true }

  const formattedRecord = {
    schedule_id: scheduleId.value,
    recording_info: {

      discussion: getObjectFromArray(selectedDiscussion.value),
      clinical_staging: clinicalObject,
      referrals: getObjectFromArray(selectedReferral.value),
      recommendations: getObjectFromArray(selectedRecommendation.value),
      tnm: tnmValue.value,
      notes: notesValue.value,
    },
  }

  const schedulePayload = [{
    tb_id: patient.value.tb_id,
    date: formatDateToTBPayload(patient.value.date),
    notes: patient.value.notes,
    schedule_id: patient.value.schedule_id,
    priority: selectedPriority.value.priority,
  }]

  // Only add diagnosis to the payload if it exists to prevent crash
  if (selectedDiagnosis.value?.description) {
    schedulePayload[0].diagnosis = selectedDiagnosis.value.description
  }

  const patchRecordResponse = await patchRecordData([formattedRecord])
  const patchSchedule = await patchPatientSchedule(schedulePayload)

  if (patchRecordResponse.detail || patchSchedule.detail) {
    showToast('error', 'Error in recording the session for the patient')
    return
  }

  showToast('success', 'Successfully saved the Tumor Board discussion')
  isSaveClicked.value = true
}

const handleNextPatient = () => {
  isSaveClicked.value = false
  selectedDiagnosis.value = ''
  selectedDiscussion.value = []
  selectedClinicalStage.value = ''
  selectedReferral.value = []
  selectedRecommendation.value = []
  selectedPriority.value = ''
  notesValue.value = ''
  tnmValue.value = ''
  dynamicNotesButtonLabel.value = 'Click to Add Notes'
  dynamicTNMButtonLabel.value = 'Click to Add TNM'

  if (hasNextPatient.value) {
    const nextPatientIndex = patientsFromAgenda.value.findIndex((p) => p.id === patient.value.id)
    const nextPatient = patientsFromAgenda.value[nextPatientIndex + 1]
    store.dispatch('setSessionPatientFromAgenda', { patient: nextPatient, publish: true })
  }
}
</script>

<style scoped>
.label-span {
  color: var(--Grey600);
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
}

.current-session-div {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-shrink: 0;
  align-self: stretch;
  gap: 12px;
}

.custom-placeholder-color ::placeholder {
  overflow: hidden;
  color: var(--Primary);
  text-overflow: ellipsis;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.24px;
  white-space: nowrap;
}

.hover-effect {
  margin-right: 4%;
  margin-bottom: 4%;
  cursor: pointer;
}

.backicon-notes-div {
  display: flex;
  align-items: center;
}

.current-session-save-button-div {
  margin-left: -7%;
  display: flex;
}

.button-class {
  width: 80%;
}
</style>

<style>
.current-session-multiselect-div .current-session-multiselect {
  width: 220px;
  max-width: 220px;
  height: 35px;
  font-size: 12px;
}

.current-session-multiselect-div .p-multiselect .p-multiselect-label.p-placeholder {
  color: var(--Primary);
  font-size: 12px;
  display: flex;
  padding: 8px;
}

.current-session-multiselect-div .p-multiselect .p-multiselect-label {
  display: flex;
}

.p-multiselect-label-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.p-multiselect-panel .p-multiselect-items {
  padding: 0;
  font-size: 12px;
}

.p-multiselect-panel .p-multiselect-items .p-multiselect-item {
  white-space: pre-line;
}

.current-session-dropdown-div .p-dropdown {
  width: 220px;
  height: 35px;
}

.current-session-dropdown-div .p-dropdown-label {
  font-size: 12px;
}

.current-session-notes-button-div .p-button-outlined {
  border: 1px solid var(--Grey400);
  width: 220px;
  max-width: 220px;
}

.p-multiselect-items-wrapper {
  max-width: 220px;
}

.current-session-notes-button-div .p-button-label {
  max-width: 235px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
  font-weight: 400;
}
</style>
